/**
 * Gets the transport mode for overnight journeys
 * @param {string} transportMode - The transport mode (e.g., 'TRAIN', 'BUS', 'FERRY')
 * @returns {string} The overnight transport mode
 */
export function getOvernightTransportMode(transportMode) {
  if (transportMode === 'TRAIN') return 'SLEEPER';
  if (transportMode === 'BUS') return 'OVERNIGHT_BUS';
  if (transportMode === 'FERRY') return 'OVERNIGHT_FERRY';
  return transportMode; // non-vehicle modes (e.g. WALK) span nights without becoming a sleeper journey
}

const SLEEPER_MODES = ['SLEEPER', 'OVERNIGHT_BUS', 'OVERNIGHT_FERRY'];

/**
 * Determines whether a leg's transport modes represent an overnight/sleeper journey
 * @param {Array<string>} modeOfTransports - The leg's transport modes
 * @returns {boolean} True if any mode is a sleeper/overnight vehicle mode
 */
export function isSleeperLeg(modeOfTransports) {
  return (modeOfTransports ?? []).some((mode) => SLEEPER_MODES.includes(mode));
}

/**
 * Gets the transport modes for a leg, handling overnight journeys and various fallback cases
 * @param {Object} leg - The leg object containing transport information
 * @returns {Array<string>} Array of transport modes
 */
export function getTransportModes(leg) {
  if (leg.transportMode) {
    // Handle overnight journeys
    if (leg.numberOfNights > 0) {
      return [getOvernightTransportMode(leg.transportMode)];
    }
    return [leg.transportMode];
  }

  if (leg.simpleItineraryItems) {
    return leg.simpleItineraryItems.map(({ Mode }) => Mode);
  }

  if (leg.itemStartDate !== leg.itemEndDate) {
    return ['SLEEPER'];
  }

  return ['JOURNEY'];
}
